import React from 'react';
import SlideUpDialog from 'components/Dialoges/SlideUpDialog';
import styles from '../Common/slideUpPopup.module.scss';
import { Autocomplete, FormControlLabel, Grid, Switch } from '@mui/material';
import { BankDetailsInitialState, indianBanks } from 'pages/CashAndBankBook/constants';
import { useAppContext } from 'contexts/AppContext';
import { ApiError } from 'services/api/base';
import { CashAndBankBookService } from 'services/api';
import { SBankDetails } from 'pages/CashAndBankBook/types';
import TextField from '../../../../components/FormControl/TextField';

interface AddBankModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const AddBankModal: React.FC<AddBankModalProps> = (props) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [bankDetails, setBankDetails] = React.useState<SBankDetails>(BankDetailsInitialState);

  const {
    snack: { show: showSnackbar },
  } = useAppContext();

  const handleSave = async () => {
    const validate = () => bankDetails.name && bankDetails.accountNo && bankDetails.ifscCode;

    const data = {
      name: bankDetails.name,
      isHO: bankDetails.isHO,
      accountNo: bankDetails.accountNo,
      ifscCode: bankDetails.ifscCode,
      openingBal: Number(bankDetails.openingBal),
    };
    try {
      setIsLoading(true);
      if (validate()) {
        setIsValid(true);
        await CashAndBankBookService.saveBankDetails(data);
        showSnackbar('Bank added Successfully', 'success');

        props.onClose();
        props.onSubmit();
      } else {
        setIsValid(false);
        showSnackbar('Please fill all the required fields', 'error');
      }
    } catch (error) {
      showSnackbar((error as ApiError).userMessage, 'error');
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SlideUpDialog
      open={props.open}
      onClose={props.onClose}
      title={`Add Bank Account`}
      containerClass={styles.container}
      actionText={'Add'}
      actionType={'primary'}
      onAction={handleSave}
      loading={isLoading}
    >
      <Grid container spacing={2} className={styles.inputSection} alignItems='center'>
        <Grid item xs={6}>
          <Autocomplete
            freeSolo
            value={bankDetails.name}
            options={indianBanks}
            onChange={(event, newValue) => {
              setBankDetails((prev) => ({ ...prev, name: newValue || '' }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Bank Name'
                variant='standard'
                onChange={(event) => {
                  setBankDetails((prev) => ({ ...prev, name: event.target.value }));
                }}
                className={styles.inputBox}
                error={!isValid && !bankDetails.name}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Switch
                checked={bankDetails.isHO}
                onChange={(e) => setBankDetails((prev) => ({ ...prev, isHO: e.target.checked }))}
              />
            }
            label='Mark as Head Office'
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label='Account Number'
            variant='standard'
            value={bankDetails.accountNo}
            onChange={(event) => {
              setBankDetails((prev) => ({ ...prev, accountNo: event.target.value }));
            }}
            className={styles.inputBox}
            error={!isValid && !bankDetails.accountNo}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label='IFSC Code'
            variant='standard'
            value={bankDetails.ifscCode}
            onChange={(event) => {
              setBankDetails((prev) => ({ ...prev, ifscCode: event.target.value }));
            }}
            className={styles.inputBox}
            error={!isValid && !bankDetails.ifscCode}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label='Opening Balance'
            variant='standard'
            inputType='decimal'
            value={bankDetails.openingBal}
            onChange={(event) => {
              setBankDetails((prev) => ({ ...prev, openingBal: event.target.value }));
            }}
            className={styles.inputBox}
          />
        </Grid>
      </Grid>
    </SlideUpDialog>
  );
};

export default AddBankModal;
